import { createFileRoute, Link } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { MapPin, Phone, MessageSquare } from "lucide-react";
import { LOCATION_GROUPS, LOCATIONS } from "@/lib/locations";
import { businessNode, canonical } from "@/lib/seo";
import { getSiteSettings } from "@/lib/site-settings.functions";

const TITLE = "Roofing Service Areas Across Arizona | Durafoam Roofing";
const DESCRIPTION =
  "Every Arizona city and neighborhood Durafoam Roofing serves — from Phoenix, Scottsdale, and the East Valley to Prescott, Sedona, and Tucson. Family-owned since 1989.";

const GROUP_BLURBS: Record<string, string> = {
  "phoenix-central":
    "Our home turf. We run daily inspection, repair, and recoat routes throughout central Phoenix, from Sunnyslope and Moon Valley down through Arcadia and Ahwatukee.",
  "east-valley":
    "Scottsdale, Mesa, Chandler, Gilbert, and Queen Creek — tile-underlayment work, foam recoats, and full replacements across the East Valley.",
  "north-scottsdale":
    "Custom homes and country-club communities from Cave Creek and Carefree through Troon, Desert Mountain, and Silverleaf, where HOA specs and tile detail matter.",
  "west-valley":
    "Glendale, Peoria, Surprise, Goodyear, Buckeye — foam installs, recoats, and tile work through the growing West Valley.",
  "sun-city":
    "Foam recoats and full replacements throughout Sun City, Sun City West, Sun City Grand, Vistancia, and Anthem — communities where foam is the default flat-roof system.",
  "outlying-arizona":
    "Beyond the Valley: Wickenburg, Casa Grande, Sedona, Prescott, Prescott Valley, Tucson, Lake Havasu, Parker, and Yuma. Foam-specific projects where our travel makes sense.",
};

export const Route = createFileRoute("/service-areas")({
  loader: () => getSiteSettings(),
  head: () => {
    const url = canonical("/service-areas");
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Durafoam Roofing Service Areas",
      itemListElement: LOCATION_GROUPS.flatMap((g) =>
        g.slugs
          .map((s) => LOCATIONS[s])
          .filter((l): l is NonNullable<typeof l> => Boolean(l))
          .map((loc, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: loc.displayName ?? loc.name,
            url: canonical(loc.path),
          })),
      ),
    };
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(businessNode({ url: "/service-areas" })) },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
      ],
    };
  },
  component: ServiceAreasPage,
});

function ServiceAreasPage() {
  const totalCities = LOCATION_GROUPS.reduce((sum, g) => sum + g.slugs.length, 0);

  return (
    <>
      <Breadcrumbs items={[{ href: "/service-areas", label: "Service Areas" }]} />

      {/* Hero */}
      <section className="bg-primary text-white">
        <Container className="py-12 md:py-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-accent">Service Areas</p>
          <h1 className="mt-2 text-3xl font-bold md:text-4xl lg:text-5xl">
            Roofing Service Areas Across Arizona
          </h1>
          <p className="mt-4 max-w-3xl text-white/85 md:text-lg">
            Durafoam Roofing has been serving the Phoenix Valley since 1989 — over 30,000 roofs
            inspected, repaired, coated, and replaced across {totalCities}+ cities and communities.
            Find your area below to see local project details, or reach out and we'll tell you
            straight whether we cover you.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary">
              Get a free roof inspection
            </ButtonLink>
            <a
              href="tel:+14809415373"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </a>
            <a
              href="sms:+14809415373"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 sm:hidden"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Text us
            </a>
          </div>
        </Container>
      </section>

      {/* Region jump nav */}
      <section className="border-b border-border bg-white">
        <Container className="py-4">
          <ul className="flex flex-wrap gap-2 text-sm">
            {LOCATION_GROUPS.map((group) => (
              <li key={group.id}>
                <a
                  href={`#${group.id}`}
                  className="inline-flex items-center rounded-full border border-border bg-[var(--surface)] px-3 py-1.5 font-medium text-primary hover:border-accent hover:text-accent"
                >
                  {group.label}
                  <span className="ml-2 text-xs text-[var(--muted-foreground)]">
                    {group.slugs.length}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Region sections */}
      <section className="bg-[var(--surface)]">
        <Container className="py-12 md:py-16">
          <div className="space-y-12">
            {LOCATION_GROUPS.map((group) => {
              const cities = group.slugs
                .map((s) => LOCATIONS[s])
                .filter((l): l is NonNullable<typeof l> => Boolean(l));
              if (cities.length === 0) return null;
              return (
                <div key={group.id} id={group.id} className="scroll-mt-24">
                  <div className="flex items-center gap-2 text-accent">
                    <MapPin className="h-5 w-5" aria-hidden="true" />
                    <p className="text-xs font-semibold uppercase tracking-wider">Region</p>
                  </div>
                  <h2 className="mt-1 text-2xl font-bold text-primary md:text-3xl">
                    {group.label}
                  </h2>
                  {GROUP_BLURBS[group.id] && (
                    <p className="mt-2 max-w-3xl text-[var(--muted-foreground)]">
                      {GROUP_BLURBS[group.id]}
                    </p>
                  )}
                  <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {cities.map((loc) => (
                      <li key={loc.slug}>
                        <Link
                          to={loc.path}
                          className="group block h-full rounded-xl border border-border bg-white p-4 transition hover:border-accent hover:shadow-sm"
                        >
                          <p className="font-semibold text-primary group-hover:text-accent">
                            {loc.name}
                          </p>
                          <p className="mt-1 line-clamp-2 text-sm text-[var(--muted-foreground)]">
                            {loc.subhead}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white">
        <Container className="py-12 md:py-14 text-center">
          <h2 className="text-2xl font-bold md:text-3xl">Don't see your city?</h2>
          <p className="mt-3 max-w-2xl mx-auto text-white/85">
            We travel for foam-specific projects across Arizona and add new areas all the time.
            Give us a call — if we can't help, we'll point you to someone honest who can.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary">
              Request an inspection
            </ButtonLink>
            <a
              href="tel:+14809415373"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </a>
          </div>
        </Container>
      </section>
    </>
  );
}
